import { api, buildQueryString } from '../core';
import { MEDICAL_RECORDS_TAG } from '../constants';
import { setMedicalRecordsList, setMedicalRecordsRowCount } from '@/store';

export interface MedicalRecordUploadedBy {
  userId: string;
  userName: string;
}

export interface MedicalRecordCheckedBy {
  userId: string;
  userName: string;
}

export interface MedicalRecordItem {
  caseId: string;
  caseName: string;
  caseStatus: string;
  checkCompleted: string | null;
  checkStarted: string | null;
  checkedBy: MedicalRecordCheckedBy | null;
  documentId: string | null;
  documentName: string;
  documentSize: number;
  documentStatus: string;
  documentProcessingFailureReason: string | null;
  pageCount: number | null;
  providerId: string;
  providerName: string;
  providerType: string;
  unassignedDocumentId: string | null;
  uploadedAt: string | null;
  uploadedBy: MedicalRecordUploadedBy;
}

export interface MedicalRecordsResponse {
  medicalRecords: MedicalRecordItem[];
  numberOfResults: number;
}

type MedicalRecordsProps = {
  pageIndex: number;
  pageSize: number;
  orderBy?: string;
  orderDirection?: string;

  nameFilter?: string;
  providerId?: string[];
  providerType?: string[];
  documentStatus?: string[];
  caseStatus?: string[];
  uploadedBy?: string[];
  checkedBy?: string[];
  minUploadDate?: string;
  maxUploadDate?: string;
};

const medicalRecordsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getMedicalRecords: builder.query<MedicalRecordsResponse, MedicalRecordsProps>({
      query: (params: MedicalRecordsProps) => {
        const queryString = buildQueryString(params);

        return {
          url: `medical-records?${queryString}`,
          method: 'GET',
        };
      },
      providesTags: (_result, _err, { pageIndex, pageSize, orderBy }) => [
        {
          type: MEDICAL_RECORDS_TAG,
          pageIndex: pageIndex,
          pageSize: pageSize,
          orderBy: orderBy,
        },
      ],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          if (data && 'medicalRecords' in data && 'numberOfResults' in data) {
            dispatch(setMedicalRecordsList(data.medicalRecords));
            dispatch(setMedicalRecordsRowCount(data.numberOfResults));
          }
        } catch (error) {
          console.error('Error in getMedicalRecords in onQueryStarted:', error);
        }
      },
      extraOptions: { maxRetries: 3 },
    }),
  }),
});

/**
 * Export hooks for usage in functional components, which are
 * auto-generated based on the defined endpoints
 */
export const {
  useLazyGetMedicalRecordsQuery,
  util: { invalidateTags: medicalRecordsInvalidateTags },
} = medicalRecordsApi;

export const { getMedicalRecords } = medicalRecordsApi.endpoints;
